import { useEffect, useRef } from "react";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import type { VesselNominationOutletContext } from "@features/vessel-nomination/add/constants";
import { FORM_FIELDS } from "@features/vessel-nomination/add/constants";
import type { VesselNominationFormData } from "@features/vessel-nomination/add/schema/vesselNominationFormSchema";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { LANG_BASE } from "../PreArrivalQuestionnaire";

const DepartureDraught = () => {
  const { t } = useTranslation();
  const { formData, updateFormData } = useOutletContext<VesselNominationOutletContext>();
  const { trigger, watch } = formData;

  const departureFwd = watch(FORM_FIELDS.DEPARTURE_FWD);
  const departureMid = watch(FORM_FIELDS.DEPARTURE_MID);
  const departureAft = watch(FORM_FIELDS.DEPARTURE_AFT);
  const previousValuesRef = useRef({
    departureFwd,
    departureMid,
    departureAft,
  });

  useEffect(() => {
    const previousValues = previousValuesRef.current;

    if (departureFwd !== previousValues.departureFwd) {
      if (departureMid) void trigger(FORM_FIELDS.DEPARTURE_MID);
      if (departureAft) void trigger(FORM_FIELDS.DEPARTURE_AFT);
    }

    if (departureMid !== previousValues.departureMid) {
      if (departureFwd) void trigger(FORM_FIELDS.DEPARTURE_FWD);
      if (departureAft) void trigger(FORM_FIELDS.DEPARTURE_AFT);
    }

    if (departureAft !== previousValues.departureAft) {
      if (departureFwd) void trigger(FORM_FIELDS.DEPARTURE_FWD);
      if (departureMid) void trigger(FORM_FIELDS.DEPARTURE_MID);
    }

    previousValuesRef.current = { departureFwd, departureMid, departureAft };
  }, [departureFwd, departureMid, departureAft, trigger]);

  return (
    <FormProvider {...formData}>
      <FormSection
        title={t(`${LANG_BASE}.sections.departure-draught`)}
        className="grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"
      >
        <FormItem<VesselNominationFormData, typeof FORM_FIELDS.DEPARTURE_FWD>
          testId="nomination-departure-fwd-input"
          name={FORM_FIELDS.DEPARTURE_FWD}
          type="number"
          label={t(`${LANG_BASE}.fields.fwd`)}
          onFieldChange={updateFormData}
          maxDigits={8}
          decimals={3}
          adornment="m"
          thousandSeparator
          canBeginWithZero={false}
        />
        <FormItem<VesselNominationFormData, typeof FORM_FIELDS.DEPARTURE_MID>
          testId="nomination-departure-mid-input"
          name={FORM_FIELDS.DEPARTURE_MID}
          type="number"
          label={t(`${LANG_BASE}.fields.mid`)}
          onFieldChange={updateFormData}
          maxDigits={8}
          decimals={3}
          adornment="m"
          thousandSeparator
          canBeginWithZero={false}
        />
        <FormItem<VesselNominationFormData, typeof FORM_FIELDS.DEPARTURE_AFT>
          testId="nomination-departure-aft-input"
          name={FORM_FIELDS.DEPARTURE_AFT}
          type="number"
          label={t(`${LANG_BASE}.fields.aft`)}
          onFieldChange={updateFormData}
          maxDigits={8}
          decimals={3}
          adornment="m"
          thousandSeparator
          canBeginWithZero={false}
        />
      </FormSection>
    </FormProvider>
  );
};

export default DepartureDraught;
